import React, { useState } from "react";
import { TDealer, TSpareDependency, TTicket } from "../../../types";
import { Button, Col, Collapse, Row, Statistic, Table, Tag } from "antd";
import DateUtils from "../../../utils/date";
import { dateOnlyFormat } from "../../../utils/constants";
import {
  BuildOutlined,
  HistoryOutlined,
  SettingOutlined,
  ShopOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import {
  eOTHERREQUESTTYPE,
  eSERVICEREQUEST,
  eTICKETCLOSINGMODE,
} from "../../../utils/enum";
import ServiceRequestHistory from "./ticket-history";
import { getStatusColor } from "../../../utils/ticket-status";
import SparePartsTable from "./spare-approval";
import CustomModal from "../../../components/modal";

type TProps = {
  selectedDealer?: TDealer | null;
  ticketDetails?: TTicket | null;
  spareDetails?: TSpareDependency[];
  needDefaultOpen?: boolean;
};
function DealerDetails({
  selectedDealer,
  ticketDetails,
  spareDetails,
  needDefaultOpen = true,
}: TProps) {
  const { Panel } = Collapse;

  const [isSpareApprovalModalVisible, setSpareApprovalModalVisible] =
    useState<boolean>(false);

  const panelStyle: React.CSSProperties = {
    marginBottom: 8,
    background: "#fff",
    borderRadius: "1px",
    border: "none",
  };

  return (
    <>
      {selectedDealer ? (
        <div>
          <Collapse
            defaultActiveKey={
              needDefaultOpen === false ? ["0"] : ticketDetails ? ["3"] : ["1"]
            }
            style={{ padding: "1rem" }}
            bordered={false}
            expandIconPosition={"end"}
          >
            {ticketDetails ? (
              <Panel
                style={panelStyle}
                header={
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      color: "#333333",
                    }}
                  >
                    <ToolOutlined
                      style={{ fontSize: "29px", color: "#AA9C9C" }}
                    />
                    Ticket Details
                  </span>
                }
                key="3"
              >
                {/* Dealer details 1st ROW */}
                <Row gutter={16}>
                  <Col span={6} style={{ paddingBottom: "20px" }}>
                    <Statistic
                      title="Service Request #"
                      valueRender={() => (
                        <span>{ticketDetails?.serviceRequestNo}</span>
                      )}
                    />
                  </Col>

                  <Col span={6} style={{ paddingBottom: "20px" }}>
                    <Statistic
                      title="Customer"
                      valueRender={() => (
                        <span>{ticketDetails?.customerName}</span>
                      )}
                    />
                  </Col>

                  <Col span={6} style={{ paddingBottom: "20px" }}>
                    <Statistic
                      title="Ticket Type"
                      valueRender={() => (
                        <>
                          {ticketDetails?.ticketType ===
                          eSERVICEREQUEST.OTHER ? (
                            <span>
                              {ticketDetails?.otherTktReqType ===
                              eOTHERREQUESTTYPE.PM
                                ? "PM"
                                : ticketDetails?.otherTktReqType ===
                                    eOTHERREQUESTTYPE.CHECKCALIBRATION
                                  ? "Check Calibration"
                                  : ticketDetails?.otherTktReqType ===
                                      eOTHERREQUESTTYPE.GDATA
                                    ? "G-Data"
                                    : "Other"}
                            </span>
                          ) : (
                            <span>{ticketDetails?.ticketTypeName}</span>
                          )}
                        </>
                      )}
                    />
                  </Col>

                  <Col span={6} style={{ paddingBottom: "20px" }}>
                    <Statistic
                      title="Dealer"
                      valueRender={() => (
                        <span>{ticketDetails?.dealerDetails?.dealerName}</span>
                      )}
                    />
                  </Col>

                  <Col span={6} style={{ paddingBottom: "20px" }}>
                    <Statistic
                      title="Customer Ticket No"
                      valueRender={() => (
                        <span>{ticketDetails?.custTicketNo}</span>
                      )}
                    />
                  </Col>

                  <Col span={6} style={{ paddingBottom: "20px" }}>
                    <Statistic
                      title="Issue"
                      valueRender={() => (
                        <span>{ticketDetails?.issueCatName}</span>
                      )}
                    />
                  </Col>

                  <Col span={6} style={{ paddingBottom: "20px" }}>
                    <Statistic
                      title="Current Status"
                      valueRender={() => (
                        <Tag
                          color={getStatusColor(
                            ticketDetails.currentStatusName
                          )}
                        >
                          {ticketDetails.currentStatusName?.toLocaleUpperCase()}
                        </Tag>
                      )}
                    />
                  </Col>

                  <Col span={3} style={{ paddingBottom: "20px" }}>
                    <Statistic
                      title="Created On"
                      valueRender={() => (
                        <span>
                          {ticketDetails?.createdOn
                            ? DateUtils.format(
                                ticketDetails?.createdOn,
                                dateOnlyFormat
                              )
                            : "-"}
                        </span>
                      )}
                    />
                  </Col>
                  <Col span={3} style={{ paddingBottom: "20px" }}>
                    <Statistic
                      title="Raised On"
                      valueRender={() => (
                        <span>
                          {ticketDetails?.ticketCreationDate
                            ? DateUtils.format(
                                ticketDetails?.ticketCreationDate,
                                dateOnlyFormat
                              )
                            : "-"}
                        </span>
                      )}
                    />
                  </Col>
                  <Col span={6} style={{ paddingBottom: "20px" }}></Col>
                </Row>

                <Row gutter={16}>
                  {ticketDetails?.targetClosingDate ? (
                    <Col span={6} style={{ paddingBottom: "20px" }}>
                      <Statistic
                        title="Target Closing Date"
                        valueRender={() => (
                          <span>
                            {ticketDetails?.targetClosingDate
                              ? DateUtils.format(
                                  ticketDetails?.targetClosingDate,
                                  dateOnlyFormat
                                )
                              : null}
                          </span>
                        )}
                      />
                    </Col>
                  ) : null}

                  <Col
                    span={ticketDetails?.targetClosingDate ? 6 : 12}
                    style={{ paddingBottom: "20px" }}
                  >
                    <Statistic
                      title="Issue Description"
                      valueRender={() => (
                        <span>{ticketDetails?.issueDescription}</span>
                      )}
                    />
                  </Col>

                  <Col span={12} style={{ paddingBottom: "20px" }}>
                    <Statistic
                      title="Remarks"
                      valueRender={() => <span>{ticketDetails?.remarks}</span>}
                    />
                  </Col>
                </Row>

                {ticketDetails?.subIssueCatOneList?.length ? (
                  <Row gutter={16}>
                    <Col span={12} style={{ paddingBottom: "20px" }}>
                      <Statistic
                        title="Sub Issue Category One"
                        valueRender={() => (
                          <span>
                            {ticketDetails?.subIssueCatOneList
                              ?.map((item) => item.name)
                              .join(", ")}
                          </span>
                        )}
                      />
                    </Col>

                    <Col span={12} style={{ paddingBottom: "20px" }}>
                      <Statistic
                        title="Sub Issue Category Two"
                        valueRender={() => (
                          <span>
                            {ticketDetails?.subIssueCatTwoList
                              ?.map((item) => item.name)
                              .join(", ")}
                          </span>
                        )}
                      />
                    </Col>
                  </Row>
                ) : null}

                {ticketDetails?.closingMode ? (
                  <Row gutter={16}>
                    <Col span={6} style={{ paddingBottom: "20px" }}>
                      <Statistic
                        title="Closing Mode"
                        valueRender={() => (
                          <span>
                            {ticketDetails?.closingMode ===
                            eTICKETCLOSINGMODE.INPERSONSUPPORT
                              ? "In Person"
                              : "Online"}
                          </span>
                        )}
                      />
                    </Col>

                    <Col span={18} style={{ paddingBottom: "20px" }}>
                      <Statistic
                        title="Closing Comments"
                        valueRender={() => (
                          <span>{ticketDetails?.closingComment}</span>
                        )}
                      />
                    </Col>
                  </Row>
                ) : null}
              </Panel>
            ) : null}

            {/* Dealer Details Accordion */}
            <Panel
              style={panelStyle}
              header={
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    color: "#333333",
                  }}
                >
                  <ShopOutlined
                    style={{ fontSize: "29px", color: "#AA9C9C" }}
                  />
                  Dealer Details
                </span>
              }
              key="1"
            >
              {/* Dealer details 1st ROW */}
              <Row gutter={16}>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Dealer Name"
                    valueRender={() => (
                      <span>{selectedDealer?.dealerName}</span>
                    )}
                  />
                </Col>

                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Dealer Code"
                    valueRender={() => (
                      <span>{selectedDealer?.dealerCode}</span>
                    )}
                  />
                </Col>

                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Depot Code"
                    valueRender={() => <span>{selectedDealer?.depotCode}</span>}
                  />
                </Col>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="DIV/Region"
                    valueRender={() => (
                      <span>{selectedDealer?.regionName}</span>
                    )}
                  />
                </Col>
              </Row>
              {/* Dealer details 2nd ROW */}
              <Row gutter={16}>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="City"
                    valueRender={() => (
                      <span>{selectedDealer?.districtName}</span>
                    )}
                  />
                </Col>

                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Pin Code"
                    valueRender={() => <span>{selectedDealer?.zipcode}</span>}
                  />
                </Col>

                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="State"
                    valueRender={() => <span>{selectedDealer?.stateName}</span>}
                  />
                </Col>

                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Location Type"
                    valueRender={() => (
                      <span>{selectedDealer?.locationTypeName}</span>
                    )}
                  />
                </Col>
              </Row>
              {/* Dealer details 3rd ROW */}
              <Row gutter={16}>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Contact Name"
                    valueRender={() => (
                      <span>{selectedDealer?.contactName}</span>
                    )}
                  />
                </Col>

                <Col span={6}>
                  <Statistic
                    title="Dealer Contact No"
                    valueRender={() => <span>{selectedDealer?.contactNo}</span>}
                  />
                </Col>

                <Col span={6}>
                  <Statistic
                    title="Dealer Email"
                    valueRender={() => (
                      <span>{selectedDealer?.dealerEmail}</span>
                    )}
                  />
                </Col>

                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Service Partner"
                    valueRender={() => (
                      <span>{selectedDealer?.servicePartnerName}</span>
                    )}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={18} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Address"
                    valueRender={() => <span>{selectedDealer?.address}</span>}
                  />
                </Col>

                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic title="SLA" value={`${selectedDealer?.sla} hrs`} />
                </Col>
              </Row>
            </Panel>

            {/* Machine Details Accordion */}
            <Panel
              style={panelStyle}
              header={
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    color: "#333333",
                  }}
                >
                  <SettingOutlined
                    style={{ fontSize: "29px", color: "#AA9C9C" }}
                  />
                  Machine Details
                </span>
              }
              key="2"
            >
              {/* Machine details 1st ROW */}

              <Row gutter={16}>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Machine Type"
                    valueRender={() => (
                      <span>{selectedDealer?.machineTypeName}</span>
                    )}
                  />
                </Col>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Model"
                    valueRender={() => (
                      <span>{selectedDealer?.productName}</span>
                    )}
                  />
                </Col>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Machine Serial No"
                    valueRender={() => (
                      <span>{selectedDealer?.machineSerialNo}</span>
                    )}
                  />
                </Col>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Date of Installation"
                    valueRender={() => (
                      <span>
                        {selectedDealer?.installationDate
                          ? DateUtils.format(
                              selectedDealer?.installationDate,
                              dateOnlyFormat
                            )
                          : "-"}
                      </span>
                    )}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Gyro"
                    valueRender={() => (
                      <span>{selectedDealer?.gyro ? "Yes" : "No"}</span>
                    )}
                  />
                </Col>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Gyro Type"
                    valueRender={() => <span>{selectedDealer?.gyroType}</span>}
                  />
                </Col>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Gyro SN"
                    valueRender={() => (
                      <span>{selectedDealer?.gyroSerialNo}</span>
                    )}
                  />
                </Col>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="UPS"
                    valueRender={() => (
                      <span>{selectedDealer?.ups ? "Yes" : "No"}</span>
                    )}
                  />
                </Col>

                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Galileo"
                    valueRender={() => (
                      <span>{selectedDealer?.galileo ? "Yes" : "No"}</span>
                    )}
                  />
                </Col>

                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Warranty Date"
                    valueRender={() => (
                      <span>
                        {selectedDealer?.warrantyDate
                          ? DateUtils.format(
                              selectedDealer?.warrantyDate,
                              dateOnlyFormat
                            )
                          : "-"}
                      </span>
                    )}
                  />
                </Col>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="AMC From"
                    value={
                      selectedDealer?.amcFromDate
                        ? DateUtils.format(
                            selectedDealer?.amcFromDate,
                            dateOnlyFormat
                          )
                        : "-"
                    }
                  />
                </Col>
                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="AMC To"
                    value={
                      selectedDealer?.amcToDate
                        ? DateUtils.format(
                            selectedDealer?.amcToDate,
                            dateOnlyFormat
                          )
                        : "-"
                    }
                  />
                </Col>

                <Col span={6} style={{ paddingBottom: "20px" }}>
                  <Statistic
                    title="Last PM Date"
                    value={
                      selectedDealer?.lastPM
                        ? DateUtils.format(
                            selectedDealer?.lastPM,
                            dateOnlyFormat
                          )
                        : "-"
                    }
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Status"
                    valueRender={() => (
                      <Tag
                        color={
                          selectedDealer?.isActive
                            ? "var(--bs-primary)"
                            : "#f50"
                        }
                      >
                        {selectedDealer?.isActive ? "Active" : "Inactive"}
                      </Tag>
                    )}
                  />
                </Col>
              </Row>
            </Panel>

            {/* SPARE DEPENDENCY DETAILS ACCORDION */}
            {spareDetails?.length ? (
              <Panel
                style={panelStyle}
                header={
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      color: "#333333",
                    }}
                  >
                    <BuildOutlined
                      style={{ fontSize: "29px", color: "#AA9C9C" }}
                    />
                    Spare Parts Details
                  </span>
                }
                key="5"
              >
                <div>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      <Button
                        onClick={() => {
                          setSpareApprovalModalVisible(true);
                        }}
                      >
                        Price History
                      </Button>
                    </div>
                    <Table
                      dataSource={spareDetails}
                      pagination={false}
                      columns={[
                        {
                          width: 100,
                          title: "Sr. No.",
                          key: "index",
                          render: (_: any, __: any, index: number) => index + 1,
                        },
                        {
                          width: 300,
                          title: "Product Name",
                          dataIndex: "productName",
                          key: "productName",
                          render: (__: any, spareInfo: TSpareDependency) =>
                            spareInfo?.partsInfo?.productName,
                        },
                        {
                          width: 300,
                          title: "Spare Part #",
                          dataIndex: "sparePartNo",
                          key: "sparePartNo",
                          render: (__: any, spareInfo: TSpareDependency) =>
                            spareInfo?.partsInfo?.partNumber,
                        },
                        {
                          title: "Spare Part Description",
                          dataIndex: "sparePartDescription",
                          key: "sparePartDescription",
                          render: (__: any, spareInfo: TSpareDependency) =>
                            spareInfo?.partsInfo?.partDescription,
                        },
                      ]}
                    />
                  </div>
                </div>
                <CustomModal
                  title={`Spare Review for #${ticketDetails?.serviceRequestNo}`}
                  isOpen={isSpareApprovalModalVisible}
                  disableOk={true}
                  hideCancelButton={true}
                  hideOkButton={true}
                  isFullScreen
                  okText="Submit"
                  onOk={async () => {
                    console.log("OK Pressed");
                  }}
                  onCancel={() => {
                    setSpareApprovalModalVisible(false);
                  }}
                >
                  <SparePartsTable
                    isEditable={false}
                    ticketId={ticketDetails?.id as number}
                    dealerId={ticketDetails?.dealerId as number}
                    onClose={() => setSpareApprovalModalVisible(false)}
                  />
                </CustomModal>
              </Panel>
            ) : null}

            {/* Service request history panel */}
            {ticketDetails?.ticketDetailList ? (
              <Panel
                style={panelStyle}
                header={
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      color: "#333333",
                    }}
                  >
                    <HistoryOutlined
                      style={{ fontSize: "29px", color: "#AA9C9C" }}
                    />
                    Service Request History
                  </span>
                }
                key="4"
              >
                {/* Dealer details 1st ROW */}
                <Row gutter={16}>
                  <ServiceRequestHistory
                    ticketDetails={ticketDetails?.ticketDetailList}
                  ></ServiceRequestHistory>
                </Row>
              </Panel>
            ) : null}
          </Collapse>
        </div>
      ) : null}
    </>
  );
}

export default DealerDetails;
